import { useEffect, useRef } from 'react';

import { track } from '@/lib/telemetry';
import type { WidgetIdentity } from './widget-identity';

// An impression means the user plausibly SAW the widget, not that it rendered —
// half-visible is the conventional threshold.
const IMPRESSION_VISIBILITY = 0.5;

/**
 * Emits one `new_home.widget_impression` per mount when the referenced element
 * first becomes ≥50% visible. Inert without an identity (rendered outside the
 * grid) or where IntersectionObserver is unavailable (jsdom).
 */
export function useWidgetImpression(identity: WidgetIdentity | null) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !identity || typeof IntersectionObserver === 'undefined') return;

    let seen = false;
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: IMPRESSION_VISIBILITY,
    });

    function handleIntersection(entries: IntersectionObserverEntry[]) {
      if (seen || !entries.some(meetsVisibilityThreshold)) return;
      seen = true;
      track('new_home.widget_impression', { widget: identity!.id, position: identity!.position });
      observer.disconnect();
    }

    observer.observe(element);
    return () => observer.disconnect();
  }, [identity]);

  return ref;
}

// The initial observation callback fires with the CURRENT ratio, which can be
// intersecting but below the threshold — `isIntersecting` alone would count a
// barely-peeking widget as seen.
function meetsVisibilityThreshold(entry: IntersectionObserverEntry): boolean {
  return entry.isIntersecting && entry.intersectionRatio >= IMPRESSION_VISIBILITY;
}
